import React, { useEffect, useState } from "react";
import {
  Box, Typography, Button, CircularProgress, MenuItem, Select, Avatar,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import EditIcon from "@mui/icons-material/Edit";
import { useSnackbar } from "notistack";
import { amend_booking_api, handover_candidates_api, topic_options_api } from "../../../DAL/SchoolPortal/Tutors";
import {
  border, textPrimary, textSecondary, textMuted, accent, font, warning, infoBlue,
  secondaryButtonSx, inputSx, avatarColorFor,
  filledButtonSx, ACTION_COLORS,
} from "../../../layouts/schoolPortal/schoolPortalTheme";

/**
 * The two things you could not do to a booked session.
 *
 * A confirmed session could be rescheduled, cancelled or marked a no-show,
 * and that was the whole vocabulary. An instructor who wakes up ill can only
 * CANCEL — which takes the money back out, frees a slot the student then has
 * to find again, and on a gated course leaves the next lesson locked behind a
 * session that no longer exists. "Can someone else take it?" is the normal
 * answer at every flight school and the product had no word for it.
 *
 * HANDING OVER IS NOT RESCHEDULING. The time does not change; the person
 * does, which is the one detail a student checks before walking into a room.
 *
 * NOBODY IS HIDDEN FROM THE PICKER. An instructor who is busy, or who does
 * not cover the topic, is listed with the reason rather than filtered out —
 * a picker that silently omits the obvious colleague sends someone hunting
 * for a bug that is really a clash.
 *
 * THE PRICE NEVER MOVES. Said on the screen, not just enforced on the server,
 * because the question an instructor has before pressing this is "does this
 * cost the student anything?" and a UI that stays silent is answered with a
 * guess.
 */

const H = { fontFamily: font };
const label = {
  ...H, fontSize: "10px", fontWeight: 800, letterSpacing: "0.07em",
  textTransform: "uppercase", color: textMuted,
};


/**
 * WHAT MAY BE SET COMES FROM THE SERVER, NOT FROM THIS FILE.
 *
 * This used to read a subject list out of the theme and pick UK or US by
 * region. Two things were wrong with it. A hardcoded copy of server truth
 * drifts silently, and this one had form — six of the fifteen US subject
 * gradients in that same theme file disagreed with the live site when they
 * were last checked. And the two courses are not the same SHAPE, so a list
 * was the wrong answer for one of them however accurate it was: the US course
 * is sixteen numbered lessons, each already naming its own subject. Offering
 * a free subject dropdown there lets an instructor set a session to weather
 * theory while the student's gate says Lesson 6, airport operations — with
 * the sign-off that opens Lesson 7 hanging off it.
 *
 * So the endpoint returns a MODE and this renders whatever the mode says.
 */

export default function SessionAmend({ booking, onDone, extra }) {
  const { enqueueSnackbar } = useSnackbar();
  const [panel, setPanel] = useState(null);          // "handover" | "topic"
  const [cands, setCands] = useState(null);
  const [chosen, setChosen] = useState("");
  const [reason, setReason] = useState("");
  const [subject, setSubject] = useState(booking?.subject || "");
  const [lesson, setLesson] = useState(booking?.lesson ?? "");
  const [topic, setTopic] = useState(booking?.topic || "");
  const [opts, setOpts] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (panel !== "topic" || opts || !booking?._id) return;
    topic_options_api(booking._id).then((r) => setOpts(r?.code === 200 ? r : { mode: "error", message: r?.message }));
  }, [panel, opts, booking?._id]);

  /**
   * Who could take this session, asked as the page loads rather than when the
   * button is pressed.
   *
   * It used to be fetched on click, so "Hand over" was always offered and an
   * independent instructor pressing it got told there was nobody to hand it
   * to. A control whose only outcome is that sentence should not be on the
   * page: at a one-person group, handing over is not a thing that exists.
   *
   * Asking the server rather than reading schoolType covers the case that
   * matters as well, a real school where only one instructor happens to be
   * bookable today.
   */
  useEffect(() => {
    if (!booking?._id) return;
    let cancelled = false;
    handover_candidates_api(booking._id).then((r) => {
      if (cancelled) return;
      setCands(r.code === 200 ? (r.candidates || []) : []);
    }).catch(() => { if (!cancelled) setCands([]); });
    return () => { cancelled = true; };
  }, [booking?._id]);

  // null while we are still asking. The button appears only once we know there
  // is somebody, so it never flashes in and out.
  const canHandOver = Array.isArray(cands) && cands.length > 0;

  const save = async (changes, successPanel) => {
    setBusy(true);
    try {
      const r = await amend_booking_api(booking._id, changes);
      enqueueSnackbar(r.message || "Saved", { variant: r.code === 200 ? "success" : "error" });
      if (r.code === 200) { setPanel(null); setCands(null); onDone?.(successPanel); }
    } finally { setBusy(false); }
  };

  const tiny = { ...secondaryButtonSx, minHeight: 0, py: 0.5, px: 1.2, fontSize: "12px" };

  return (
    <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${border}` }}>
      <Typography sx={{ ...label, mb: 0.9 }}>Change this session</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, flexWrap: "wrap" }}>
        {canHandOver && (
          <Button
            onClick={() => setPanel(panel === "handover" ? null : "handover")}
            startIcon={<SwapHorizIcon sx={{ fontSize: 16 }} />}
            sx={panel === "handover" ? filledButtonSx(ACTION_COLORS.handover) : tiny}
          >
            Hand over
          </Button>
        )}
        <Button
          onClick={() => setPanel(panel === "topic" ? null : "topic")}
          startIcon={<EditIcon sx={{ fontSize: 15 }} />}
          sx={panel === "topic" ? filledButtonSx(infoBlue) : tiny}
        >
          Change topic
        </Button>
        {/* Move, cancel and no-show already existed and still live in their own
            control — rebuilding them here would give this session two
            implementations of cancelling, which is one more than any session
            should have. */}
        {extra}
      </Box>

      {panel === "handover" && canHandOver && (
        <Box sx={{ mt: 1.25, p: 1.25, border: `1px solid ${border}`, borderRadius: "10px", backgroundColor: "#fff" }}>
          {!cands ? (
            <CircularProgress size={16} sx={{ color: accent }} />
          ) : !cands.length ? (
            <Typography sx={{ ...H, fontSize: "12.5px", color: textSecondary }}>
              Nobody else at this school is taking bookings, so there is no one to hand it to.
            </Typography>
          ) : (
            <>
              {cands.map((c) => (
                <Box
                  key={c.id}
                  onClick={() => c.available && setChosen(c.id)}
                  sx={{
                    display: "flex", alignItems: "center", gap: 1, p: 0.8, borderRadius: "8px",
                    cursor: c.available ? "pointer" : "not-allowed",
                    opacity: c.available ? 1 : 0.55,
                    border: `1px solid ${String(chosen) === String(c.id) ? accent : "transparent"}`,
                    backgroundColor: String(chosen) === String(c.id) ? `${accent}0d` : "transparent",
                    "&:hover": c.available ? { backgroundColor: `${accent}0d` } : {},
                  }}
                >
                  <Avatar src={c.photoUrl || undefined} sx={{ width: 26, height: 26, fontSize: "11px", fontWeight: 700, bgcolor: avatarColorFor(c.name) }}>
                    {c.name.split(" ").map((x) => x[0]).slice(0, 2).join("")}
                  </Avatar>
                  <Typography sx={{ ...H, fontSize: "12.5px", fontWeight: 700, color: textPrimary, flex: 1, minWidth: 0 }}>
                    {c.name}
                  </Typography>
                  {c.why && (
                    <Typography sx={{ ...H, fontSize: "11px", color: warning, flexShrink: 0 }}>{c.why}</Typography>
                  )}
                </Box>
              ))}
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why, in a few words — the student is not shown this"
                style={{
                  ...inputSx?.["& input"], width: "100%", marginTop: 8, padding: "8px 10px",
                  fontFamily: font, fontSize: "12.5px", border: `1px solid ${border}`, borderRadius: 8,
                }}
              />
              <Typography sx={{ ...H, fontSize: "11px", color: textSecondary, mt: 0.9, lineHeight: 1.5 }}>
                The time does not change and nothing is charged again — the student keeps the price they
                agreed. Everyone gets told, including you.
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.7, mt: 1 }}>
                <Button onClick={() => setPanel(null)} sx={tiny}>Cancel</Button>
                <Button
                  disabled={!chosen || busy}
                  onClick={() => save({ instructor: chosen, reason }, "handover")}
                  sx={filledButtonSx(ACTION_COLORS.handover)}
                >
                  Hand it over
                </Button>
              </Box>
            </>
          )}
        </Box>
      )}

      {panel === "topic" && (
        <Box sx={{ mt: 1.25, p: 1.25, border: `1px solid ${border}`, borderRadius: "10px", backgroundColor: "#fff" }}>
          {!opts ? (
            <CircularProgress size={16} sx={{ color: accent }} />
          ) : opts.mode === "error" ? (
            <Typography sx={{ ...H, fontSize: "12.5px", color: warning }}>
              {opts.message || "Could not load the options"}
            </Typography>
          ) : opts.mode === "lesson" ? (
            <>
              {/* THE LESSON IS THE ONLY CHOICE. Each one names its own subject,
                  and the subject is shown so the change does not look like it
                  did nothing. */}
              <Typography sx={{ ...label, mb: 0.6 }}>Lesson</Typography>
              <Select
                fullWidth size="small" value={lesson} onChange={(e) => setLesson(e.target.value)}
                displayEmpty sx={{ ...inputSx, mb: 1 }}
              >
                <MenuItem value=""><em>Not tied to a lesson</em></MenuItem>
                {opts.lessons.map((l) => (
                  <MenuItem key={l.n} value={l.n}>
                    {l.n}. {l.title}{l.deepDive ? " · deep dive" : ""}
                  </MenuItem>
                ))}
              </Select>
              <Typography sx={{ ...H, fontSize: "11.5px", color: textSecondary, lineHeight: 1.5 }}>
                {lesson
                  ? `Covers ${(opts.lessons.find((l) => String(l.n) === String(lesson))?.subjects || [])
                    .map((x) => x.label).join(", ") || "—"}.`
                  : opts.note}
              </Typography>
            </>
          ) : (
            <>
              <Typography sx={{ ...label, mb: 0.6 }}>Subject</Typography>
              <Select
                fullWidth size="small" value={subject}
                onChange={(e) => { setSubject(e.target.value); setTopic(""); }}
                displayEmpty sx={{ ...inputSx, mb: 1.25 }}
              >
                <MenuItem value=""><em>Not set</em></MenuItem>
                {opts.subjects.map((x) => <MenuItem key={x.key} value={x.key}>{x.label}</MenuItem>)}
              </Select>
              <Typography sx={{ ...label, mb: 0.6 }}>Topic (optional)</Typography>
              <Select
                fullWidth size="small" value={topic} onChange={(e) => setTopic(e.target.value)}
                displayEmpty disabled={!subject} sx={inputSx}
              >
                <MenuItem value=""><em>Whole subject</em></MenuItem>
                {(opts.subjects.find((x) => x.key === subject)?.topics || []).map((t) => (
                  <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>
                ))}
              </Select>
              <Typography sx={{ ...H, fontSize: "11.5px", color: textSecondary, mt: 0.8, lineHeight: 1.5 }}>
                {/* Changing the subject clears the topic, on the server too — a
                    session labelled "air law · density altitude" is not a thing. */}
                {opts.note}
              </Typography>
            </>
          )}
          <Typography sx={{ ...H, fontSize: "11px", color: textSecondary, mt: 0.9, lineHeight: 1.5 }}>
            Changing this rewrites the brief for the session. The price stays as booked.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.7, mt: 1 }}>
            <Button onClick={() => setPanel(null)} sx={tiny}>Cancel</Button>
            <Button
              disabled={busy || !opts || opts.mode === "error"}
              onClick={() => save(opts.mode === "lesson"
                ? { lesson: lesson === "" ? null : Number(lesson) }
                : { subject, topic: topic || null })}
              sx={filledButtonSx(infoBlue)}
            >
              Save
            </Button>
          </Box>
        </Box>
      )}

    </Box>
  );
}
